import { z } from 'zod';
import { brainRowProvenanceSchema } from './brain-row-provenance.js';
import { brandDesignSystemSchema, DEFAULT_BRAND_DESIGN_SYSTEM } from './brand-design-system.js';
// Spec §3.1 — the Brand Brain is the spine of every Mag interaction.
// Each top-level section maps to one JSONB column in the brand_brains table.
export const stageSchema = z.enum(['pre-revenue', 'early', 'growth', 'scale']);
const optionalUrl = z.string().url().optional();
export const productSchema = z.object({
    name: z.string().min(1),
    description: z.string().default(''),
    price_cents: z.number().int().nonnegative().optional(),
    url: optionalUrl,
});
export const competitorSchema = z.object({
    name: z.string().min(1),
    url: optionalUrl,
    why_they_win: z.string().optional(),
    why_we_win: z.string().optional(),
});
/** [channel-intake] How the business actually delivers value.
 *  Captured during the channel-intake conversation so the content-plan
 *  agent + future post-drafter know whether to propose "show your
 *  storefront", "demo the app", or "before/after of the service" —
 *  decisions that depend on the business shape, not its category.
 *
 *  Optional sub-block on identity — legacy brains (pre-channel-intake)
 *  decode cleanly without it. When absent, agents treat the shape as
 *  unknown and skip shape-conditional post ideas. */
export const businessShapeKindSchema = z.enum([
    /** Software / SaaS / app — the product is bits, demos screenshot well. */
    'digital_product',
    /** Physical good — inventory, photography, unboxing, storefront. */
    'physical_product',
    /** Founder-delivered service — consulting, agency, coaching, freelancer. */
    'service',
    /** Two-sided marketplace — listings, supply / demand both matter. */
    'marketplace',
    /** Restaurant, hospitality, in-person events — atmosphere + location-led. */
    'venue',
    /** Mixed model. Use when the brand straddles two (e.g., physical
     *  product + paid consulting). Avoid as a default — push the
     *  founder to name the leading shape. */
    'hybrid',
]);
export const serviceDeliverySchema = z.enum([
    /** Founder / team meets clients face-to-face (clinic, agency office, studio). */
    'in_person',
    /** Fully remote (zoom, async, slack-only). */
    'remote',
    /** Both, configurable per engagement. */
    'hybrid',
]);
export const businessShapeSchema = z.object({
    kind: businessShapeKindSchema,
    /** Brand has a real-world location worth showing: storefront,
     *  studio, clinic, restaurant, factory. Anchors post ideas like
     *  "let's shoot the front door this week." */
    has_physical_location: z.boolean().default(false),
    /** For service / hybrid kinds only. null on product / venue. */
    service_delivery: serviceDeliverySchema.nullable().default(null),
    /** Free-text one-liner the founder gave during intake — kept verbatim
     *  so future agents can quote it without re-deriving. Capped at the
     *  brain-identity field length budget. */
    founder_phrasing: z.string().max(280).default(''),
});
export const brandBrainIdentitySchema = z.object({
    company_name: z.string().min(1),
    products: z.array(productSchema).default([]),
    category: z.string().min(1),
    stage: stageSchema,
    positioning_statement: z.string().default(''),
    value_props: z.array(z.string()).default([]),
    competitors: z.array(competitorSchema).default([]),
    differentiators: z.array(z.string()).default([]),
    /** [channel-intake] Optional — captured during channel-intake.
     *  Absent on legacy brains; agents read as "unknown shape" and
     *  skip shape-conditional post ideas. */
    business_shape: businessShapeSchema.optional(),
});
// Tone axes: 0..10 sliders. 0 = first label, 10 = second label.
export const toneAxesSchema = z.object({
    formal_casual: z.number().int().min(0).max(10),
    serious_playful: z.number().int().min(0).max(10),
    expert_friendly: z.number().int().min(0).max(10),
});
export const voiceExampleSchema = z.object({
    text: z.string().min(1),
    context: z.string().min(1),
});
export const voiceAntiExampleSchema = z.object({
    text: z.string().min(1),
    why: z.string().min(1),
});
export const brandBrainVoiceSchema = z.object({
    tone_axes: toneAxesSchema,
    must_use_words: z.array(z.string()).default([]),
    must_avoid_words: z.array(z.string()).default([]),
    voice_examples: z.array(voiceExampleSchema).default([]),
    voice_anti_examples: z.array(voiceAntiExampleSchema).default([]),
});
export const testimonialSchema = z.object({
    quote: z.string().min(1),
    attribution: z.string().optional(),
    source_url: optionalUrl,
});
export const icpSegmentSchema = z.object({
    name: z.string().min(1),
    description: z.string().default(''),
    pains: z.array(z.string()).default([]),
    jobs_to_be_done: z.array(z.string()).default([]),
    channels: z.array(z.string()).default([]),
});
export const brandBrainCustomerSchema = z.object({
    icp_segments: z.array(icpSegmentSchema).default([]),
    testimonials: z.array(testimonialSchema).default([]),
    reviews_summary: z
        .object({
        themes: z.array(z.string()).default([]),
        avg_rating: z.number().min(0).max(5).optional(),
    })
        .default({ themes: [] }),
    support_themes: z.array(z.string()).default([]),
});
export const assetRefSchema = z.object({
    // Asset rows live in their own table later; until then, we just track URLs.
    url: z.string().url(),
    alt: z.string().optional(),
    width: z.number().int().positive().optional(),
    height: z.number().int().positive().optional(),
});
export const colorSchema = z.object({
    name: z.string().min(1),
    hex: z.string().regex(/^#[0-9a-fA-F]{6}$/),
});
export const fontSchema = z.object({
    name: z.string().min(1),
    url: optionalUrl,
    weights: z.array(z.number().int()).default([]),
});
export const brandKitSchema = z.object({
    logo_variants: z.array(assetRefSchema).default([]),
    colors: z.array(colorSchema).default([]),
    fonts: z.array(fontSchema).default([]),
});
export const legalConstraintsSchema = z.object({
    regulated_category: z.boolean().default(false),
    forbidden_claims: z.array(z.string()).default([]),
    required_disclaimers: z.array(z.string()).default([]),
});
export const brandBrainAssetsSchema = z.object({
    brand_kit: brandKitSchema.default({ logo_variants: [], colors: [], fonts: [] }),
    hero_images: z.array(assetRefSchema).default([]),
    existing_creatives: z.array(assetRefSchema).default([]),
    legal_constraints: legalConstraintsSchema.default({
        regulated_category: false,
        forbidden_claims: [],
        required_disclaimers: [],
    }),
});
// One scraped page snapshot — what the marketing site said the last
// time we read it. Mag's downstream agents (creative pipeline, copy
// drafting, etc.) read this so they don't fabricate product-specific
// content (the 4 steps, the price, the testimonial themes) when the
// founder's brief references it without spelling it out.
export const scrapedPageSchema = z.object({
    url: z.string().url(),
    title: z.string().max(500).default(''),
    description: z.string().max(2000).default(''),
    /** Plain-text body, headings + paragraph copy + list items, capped
     *  to ~12KB on extract. Verbatim — no LLM processing. */
    text: z.string().max(40_000),
    scrapedAt: z.string().datetime(),
    /** R2-hosted JPEG screenshot of the page, captured asynchronously
     *  by the scraped-page-thumbnail queue after the text extract lands.
     *  Null until the screenshot job runs (the dashboard renders a paper
     *  placeholder in that window). Optional on the wire so older rows
     *  that pre-date the screenshot feature deserialize cleanly. */
    thumbnailUrl: z.string().url().nullable().optional(),
    /** Same-site URLs this page links to — the internal link graph, kept so the
     *  site-wide audit can spot orphan pages without re-crawling.
     *
     *  OPTIONAL, and deliberately NOT `.default([])`. Rows scraped before this
     *  field existed have no link data, and collapsing that to `[]` would make
     *  them indistinguishable from a page that genuinely links nowhere — every
     *  such page would then read as an orphan. `undefined` means "we never
     *  recorded this page's links"; `[]` means "we did, and it links nowhere".
     *  The orphan check refuses to run unless every page has real data. */
    links: z.array(z.string()).max(500).optional(),
});
// History is system-written (not founder-filled). Schemas firm up when the
// campaign/concept records land in later sessions (see spec §3 history block).
// For now we accept and store anything Mag emits, schema-validated at write.
export const brandBrainHistorySchema = z.object({
    campaigns_run: z.array(z.unknown()).default([]),
    creative_concepts: z.array(z.unknown()).default([]),
    audience_tests: z.array(z.unknown()).default([]),
    channel_performance: z.record(z.string(), z.unknown()).default({}),
});
// Authority levels per spec §5. The map itself fills in once the decision
// type registry is defined; until then we accept any decision-type → level.
export const autonomyLevelSchema = z.enum(['A0', 'A1', 'A2', 'A3']);
export const authorityMapSchema = z.record(z.string(), autonomyLevelSchema);
export const founderFaceUsageSchema = z.enum(['never', 'with_approval', 'free']);
export const preferredChannelSchema = z.enum(['slack', 'email', 'web']);
// Founder pinning for creative templates (s233.32). Two knobs:
//   blocklist:           templates the brand should NEVER use. The
//                        pipeline strips these before the template-
//                        router sees its candidate list, so even a
//                        loud-brand voice won't bias toward a
//                        blocked template.
//   per_format_defaults: per-format preferred template. When set,
//                        the pipeline skips the router LLM for that
//                        format and uses the pinned template
//                        directly (saves ~$0.005/concept + gives
//                        the founder a stable look). Key is a
//                        CreativeAssetFormat string; value is a
//                        template_key from the registry. The brand-
//                        brain edit endpoint validates membership at
//                        write time — pipeline re-checks at read
//                        time so a removed template doesn't crash
//                        a render.
//
// Both knobs are optional; empty defaults preserve the
// router-as-default behavior for brands that haven't curated.
export const brandTemplatePreferencesSchema = z.object({
    blocklist: z.array(z.string().min(1).max(60)).default([]),
    per_format_defaults: z.record(z.string().min(1), z.string().min(1).max(60)).default({}),
});
const DEFAULT_TEMPLATE_PREFERENCES = {
    blocklist: [],
    per_format_defaults: {},
};
export const brandBrainPreferencesSchema = z.object({
    decision_authority_map: authorityMapSchema.default({}),
    no_go_topics: z.array(z.string()).default([]),
    must_approve_categories: z.array(z.string()).default([]),
    preferred_channel: preferredChannelSchema.default('slack'),
    working_hours_tz: z.string().default('UTC'),
    founder_face_usage: founderFaceUsageSchema.default('with_approval'),
    // s233.32 — founder pinning. Default empty so brains created before
    // this lands still validate.
    template_preferences: brandTemplatePreferencesSchema.default(DEFAULT_TEMPLATE_PREFERENCES),
});
// §3.1b Phase 1 (s232) — provenance + status semantics for every
// extractable brain row. Provenance answers "why did Mag pick this?"
// without re-running the pipeline; status `'draft'` (added in this
// phase) quarantines low-confidence rows from creative agents until
// the founder confirms or the critic/verifier promotes them.
//
// The provenance schema itself lives in brain-row-provenance.ts —
// re-exported here for back-compat so existing importers don't have
// to change. The split exists because brand-design-system.ts also
// uses provenance (for motifs), and importing back through this
// module would create an ESM init cycle.
export { brainRowProvenanceMethodSchema, brainRowProvenanceSchema, } from './brain-row-provenance.js';
// Phase 4.31 (s221) — products & services section. Distinct from the
// minimal `identity.products` block above (which only carries name +
// price + url): this is the structured library Mag extracts from the
// scraped landing pages and the founder edits in the brand hub. Used
// in template-fill prompts so creative copy references real
// offerings instead of generic claims.
//
// Status field tracks provenance across re-extractions:
//   - 'draft'  : §3.1b extracted but below AUTO_THRESHOLD — hidden
//                from creative agents until verified or reviewed.
//   - 'auto'   : Mag's extraction, untouched by the founder.
//   - 'edited' : founder edited an auto row. Sticky — re-extract
//                won't overwrite.
//   - 'manual' : founder typed from scratch. Re-extract ignores it.
//   - 'stale'  : auto row that's no longer surfaced by a fresh
//                extraction. Kept (don't silently delete) so the
//                founder can see what changed.
export const brandBrainProductKindSchema = z.enum(['product', 'service', 'package', 'collection']);
export const brandBrainProductStatusSchema = z.enum(['draft', 'auto', 'edited', 'manual', 'stale']);
export const brandBrainProductSchema = z.object({
    /** Stable id (nanoid) so the dashboard can edit + reorder without
     *  re-hydrating from name. Generated client-side on add OR by the
     *  extractor when it creates an auto row. */
    id: z.string().min(1).max(40),
    /** Display name — the canonical thing the founder calls this. */
    name: z.string().min(1).max(120),
    kind: brandBrainProductKindSchema,
    /** 1-line teaser. Used in the asset-list view + template-fill prompt. */
    shortDescription: z.string().max(400).default(''),
    /** Long-form details. Optional — only set when the founder + extractor
     *  could pull a meaningful longer description from the page. */
    longDescription: z.string().max(1500).optional(),
    /** Who this is for. Free-text, used in template-fill context. */
    audience: z.string().max(400).optional(),
    /** "$99", "from $19/mo", "free trial then $49". Free-text since
     *  brands write prices in different shapes — uruguayan brands in
     *  particular tend to spell out "USD 99/mes, IVA incluido — plan
     *  profesional" which is well over 80 chars. bound raised to 200
     *  in sync with the wire schema in products-extractor. */
    priceHint: z.string().max(200).optional(),
    /** The page on the brand's site this product was extracted from
     *  (when known). Lets the dashboard render "extracted from /camp"
     *  + lets the next extraction know where this entry came from. */
    sourceUrl: z.string().url().optional(),
    /** Free-text tags Mag applies during extraction + the founder can
     *  edit. Examples: ['lead-magnet', 'flagship', 'seasonal']. */
    tags: z.array(z.string().min(1).max(48)).default([]),
    status: brandBrainProductStatusSchema,
    /** 0–1 — how confident the extractor was in this row. Only
     *  meaningful for status='auto'/'stale'/'draft'. UI shows it as a hint. */
    confidence: z.number().min(0).max(1).default(0.7),
    /** §3.1b Phase 1 — how this row got here. Optional so legacy rows
     *  (written before s232) parse cleanly. Extractors set this on every
     *  new row; founder edits leave it untouched. */
    provenance: brainRowProvenanceSchema.optional(),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
});
export const brandBrainProductsSchema = z.array(brandBrainProductSchema).default([]);
// (s284) — structured pricing / plans section. Distinct from the
// products library on purpose: `products` is WHAT the brand sells
// (surfaces, editions, SKUs, service packages); `pricing` is HOW they
// charge for it (the plan/tier catalog + the overall pricing model).
// They change on different cadences and a single-product SaaS has one
// product but several plan tiers, so conflating them — the way the old
// extractor did when it listed "Free during beta" as a product — is
// wrong. Plan rows reuse the products status/confidence/provenance
// vocabulary so founder-edit + merge semantics are identical.
export const brandBrainPricingModelSchema = z.enum([
    /** No charge at all (open-source, fully free tool). */
    'free',
    /** Free tier + paid upgrades. */
    'freemium',
    /** Time-boxed free trial, then paid. */
    'trial',
    /** Recurring subscription (monthly / annual / per-seat). */
    'subscription',
    /** One-time purchase, no recurrence. */
    'one_time',
    /** Metered / pay-as-you-go billing. */
    'usage_based',
    /** "Contact us" / custom-quote, no public price. */
    'quote',
    /** Couldn't determine from the page. */
    'unknown',
]);
export const brandBrainPricingCadenceSchema = z.enum([
    'one_time',
    'monthly',
    'annual',
    'per_seat',
    'usage',
    'custom',
]);
export const brandBrainPricingPlanSchema = z.object({
    /** Stable id (uuid/nanoid) — same role as the products id. */
    id: z.string().min(1).max(40),
    /** Tier name as the brand writes it: "Free", "Pro", "Team",
     *  "Per-developer at GA". */
    name: z.string().min(1).max(120),
    /** Verbatim price line, free-text for the same reason products
     *  priceHint is: brands spell prices in wildly different shapes
     *  ("$99/mo", "USD 290/año, IVA incluido", "free during beta"). */
    priceHint: z.string().max(200).default(''),
    /** Billing cadence when the page states one. Omit when ambiguous. */
    cadence: brandBrainPricingCadenceSchema.optional(),
    /** Who the tier targets, when stated ("for solo founders", "teams"). */
    audience: z.string().max(400).optional(),
    /** 1-6 short bullet highlights of what the tier includes. */
    highlights: z.array(z.string().min(1).max(200)).max(6).default([]),
    /** The page this plan was extracted from. */
    sourceUrl: z.string().url().optional(),
    status: brandBrainProductStatusSchema,
    confidence: z.number().min(0).max(1).default(0.7),
    provenance: brainRowProvenanceSchema.optional(),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
});
/** The pricing section WITHOUT the section-level default.
 *
 *  Exists because `.default()` survives `.optional()`: a patch field
 *  written as `brandBrainPricingSchema.optional()` parses an absent key
 *  into `{ model:'unknown', plans:[] }` rather than leaving it absent, so
 *  a PUT that never mentioned pricing would arrive at upsertBrandBrain
 *  carrying a defined, empty pricing section — and overwrite the brand's
 *  real plans with nothing. Anywhere "absent" has to stay absent, reach
 *  for this one; `brandBrainPricingSchema` is for decoding a stored row,
 *  where the default is the point. Same split designSystem already has,
 *  which applies its default at the use site rather than baking it in. */
/** A question the brand's own pricing page asks the visitor, and which
 *  of its answers Mag was able to read.
 *
 *  Spec 621ab323 slice C. Slices A and B read the page in a browser and
 *  exercise the pricing view-switches, both of them BOUNDED — a capped
 *  number of states, and a control we cannot positively identify as a
 *  view switch is skipped rather than guessed at (never click a signup
 *  on the founder's own site). Those bounds are only safe if what falls
 *  outside them is SAID OUT LOUD. Otherwise a brand whose prices sit
 *  behind an unrecognised control is silently half-read again, which is
 *  the original failure wearing new clothes.
 *
 *  Measured: fiscalo asks "¿Aplicás a beneficios fiscales de DGI?" and
 *  answers it two ways; gestionalo prices on three dimensions at once.
 *  Neither fact was anywhere on the brain before this.
 *
 *  `assume` is the founder's answer and the only field they author. It
 *  is sticky across re-extraction exactly as an edited plan row is —
 *  re-reading the page must never overwrite "campaigns should quote the
 *  no-benefit prices". */
export const brandBrainPricingConditionSchema = z.object({
    /** Stable across re-extraction: derived from the question and its
     *  options, so the same switch keeps its founder answer even as
     *  prices change underneath it. */
    id: z.string().min(1).max(120),
    status: brandBrainProductStatusSchema.default('draft'),
    /** The page's own words. Empty when the switch carries no visible
     *  question — the options then have to speak for themselves. */
    question: z.string().max(200).default(''),
    /** Every option the switch offers, in page order. */
    options: z.array(z.string().min(1).max(80)).max(8).default([]),
    /** Options whose prices we actually captured and handed downstream. */
    read: z.array(z.string().min(1).max(80)).max(8).default([]),
    /** Options we know exist and did NOT read. The important field: an
     *  empty `read` with a non-empty `unread` is "your pricing page has a
     *  branch I have never seen". */
    unread: z.array(z.string().min(1).max(80)).max(8).default([]),
    /** Why, in plain terms, when the control was refused outright rather
     *  than merely capped — "looked like a call to action", "could not
     *  tell which option was selected". Shown to the founder, so it must
     *  read as an explanation rather than a rule name. */
    note: z.string().max(200).optional(),
    /** When this switch was last seen on the page. Also what the confirm
     *  queue stamps when the founder answers — every other draftable row
     *  carries it, and the shared transform in the drawer requires it. */
    updatedAt: z.string().default(''),
    /** FOUNDER-AUTHORED. Which option campaigns should price against.
     *  Absent until they answer; that absence is what makes the row
     *  outstanding. */
    assume: z.string().min(1).max(80).optional(),
});
export const brandBrainPricingContentSchema = z.object({
    /** The brand's overall pricing posture — read once at the section
     *  level so agents can reason "this is a freemium SaaS" without
     *  re-deriving from the plan rows. */
    model: brandBrainPricingModelSchema.default('unknown'),
    plans: z.array(brandBrainPricingPlanSchema).default([]),
    /** Conditional-pricing switches found on the page — see
     *  brandBrainPricingConditionSchema. Defaults to [] so every legacy
     *  brain decodes unchanged. */
    conditions: z.array(brandBrainPricingConditionSchema).default([]),
});
export const brandBrainPricingSchema = brandBrainPricingContentSchema
    // Object section (like designSystem), not a bare array: legacy rows
    // whose `pricing` column is absent / `{}` decode to a fully-defaulted
    // payload so every consumer sees { model:'unknown', plans:[] }.
    .default({ model: 'unknown', plans: [], conditions: [] });
// Phase 4.41 (s231) — structured team / people section. Mirrors the
// products library: Mag extracts canonical {name, role, portrait} from
// the brand's about/team page so downstream creative agents quote the
// real attribution instead of inventing roles. Founder-edit semantics
// match products exactly (status: draft | auto | edited | manual | stale).
export const brandBrainTeamMemberStatusSchema = z.enum([
    'draft',
    'auto',
    'edited',
    'manual',
    'stale',
]);
export const brandBrainTeamMemberSchema = z.object({
    /** Stable id (uuid) so the dashboard can edit + reorder without
     *  re-hydrating from name. Generated by the extractor on auto rows
     *  or client-side on manual add. */
    id: z.string().min(1).max(40),
    /** Display name — the canonical way the founder writes this person
     *  ("Sole", "María José", "Dr. K."). Verbatim from the site. */
    name: z.string().min(1).max(80),
    /** Role / title as the site states it ("Cofundadora y Directora
     *  de Operaciones", "Head of Academics", "Profe de Inglés"). Free-
     *  text — every brand writes roles differently and creative agents
     *  must quote verbatim. */
    role: z.string().min(1).max(160),
    /** Short bio sentence when the page exposes one. Optional. */
    bio: z.string().max(600).optional(),
    /** Resolved portrait URL — the asset URL of this person's photo on
     *  the site. Optional because some teams list names without
     *  portraits. The extractor correlates portraits to people by
     *  proximity in the page DOM + alt text; the dashboard lets the
     *  founder re-link. */
    portraitUrl: z.string().url().optional(),
    /** The page the entry was extracted from ("/sobre-nosotros",
     *  "/team"). Lets the dashboard show provenance + the next
     *  extraction know where this came from. */
    sourceUrl: z.string().url().optional(),
    /** Free-text tags Mag applies during extraction + the founder can
     *  edit. Examples: ['founder', 'leadership', 'instructor']. */
    tags: z.array(z.string().min(1).max(48)).default([]),
    status: brandBrainTeamMemberStatusSchema,
    /** 0–1 — extractor confidence. Only meaningful for auto/stale/draft. */
    confidence: z.number().min(0).max(1).default(0.7),
    /** §3.1b Phase 1 — how this row got here. Optional so rows written
     *  before s232 parse cleanly. */
    provenance: brainRowProvenanceSchema.optional(),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
});
export const brandBrainTeamSchema = z.array(brandBrainTeamMemberSchema).default([]);
// §3.1b Phase 7a (s233) — structured testimonials library. Separate
// from the legacy `customer.testimonials` array (which kept the
// minimal {quote, attribution, source_url} shape) so existing rows
// parse cleanly. The extractor + brand-hub UI write here; concept /
// creative agents read here in priority and fall back to the legacy
// shape when this is empty.
//
// Same status semantics as products + team:
//   - 'draft'  : extracted but below AUTO_THRESHOLD ('testimonials' tier);
//                hidden from creative agents until reviewed.
//   - 'auto'   : extractor-confident, available to creative agents.
//   - 'edited' : founder edited an auto row. Sticky.
//   - 'manual' : founder typed from scratch. Sticky.
//   - 'stale'  : auto row no longer surfaced by a fresh extraction.
export const brandBrainTestimonialStatusSchema = z.enum([
    'draft',
    'auto',
    'edited',
    'manual',
    'stale',
]);
export const brandBrainTestimonialSchema = z.object({
    /** Stable id (uuid) so the brand hub can edit + reorder. */
    id: z.string().min(1).max(40),
    /** The quote verbatim, capped at 2KB (long-form testimonials get
     *  truncated visually but stored whole). */
    quote: z.string().min(1).max(2000),
    /** Who said it ("María, fundadora de Acme" / "TechCrunch") — free-
     *  text since brands attribute differently. Optional because some
     *  testimonials run anonymously ("a happy customer"). */
    attribution: z.string().max(200).optional(),
    /** The page on the brand's site this testimonial was extracted
     *  from. Lets the brand hub deep-link to the source. */
    sourceUrl: z.string().url().optional(),
    /** Free-text tags ('product', 'service', 'press'). */
    tags: z.array(z.string().min(1).max(48)).default([]),
    status: brandBrainTestimonialStatusSchema,
    /** 0–1 — extractor confidence. */
    confidence: z.number().min(0).max(1).default(0.7),
    /** §3.1b — how this row got here. */
    provenance: brainRowProvenanceSchema.optional(),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
});
export const brandBrainTestimonialsSchema = z.array(brandBrainTestimonialSchema).default([]);
// §3.1b Phase 7b (s233a) — structured voice examples library.
// Distinct from the legacy `voice.voice_examples` (which kept its
// minimal {text, context} shape) and from `guidelines.voiceSamples`
// (which is just an unstructured string[] captured by visual-scrape).
//
// This is the curated library of "this is how we sound" anchors that
// creative agents quote as voice ground-truth — headlines, hero CTAs,
// distinctive copy snippets the brand uses verbatim on its site.
//
// Same status semantics as products/team/testimonials.
export const brandBrainVoiceExampleStatusSchema = z.enum([
    'draft',
    'auto',
    'edited',
    'manual',
    'stale',
]);
export const brandBrainVoiceExampleSchema = z.object({
    /** Stable id (uuid) so the brand hub can edit + reorder. */
    id: z.string().min(1).max(40),
    /** The verbatim voice snippet ("subite a la bici", "el inglés sí
     *  es para vos"). Short by design — long-form copy belongs in
     *  scrapedPages, not here. */
    text: z.string().min(1).max(280),
    /** Where on the site the snippet ran ("homepage hero", "/cursos
     *  CTA", "instagram bio"). Optional but strongly preferred — gives
     *  the creative agent a feel for the register. */
    context: z.string().max(200).optional(),
    /** Free-text tags ('headline', 'cta', 'hero', 'social-bio'). */
    tags: z.array(z.string().min(1).max(48)).default([]),
    status: brandBrainVoiceExampleStatusSchema,
    /** 0–1 — extractor confidence. */
    confidence: z.number().min(0).max(1).default(0.7),
    /** §3.1b — how this row got here. */
    provenance: brainRowProvenanceSchema.optional(),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
});
export const brandBrainVoiceExamplesSchema = z.array(brandBrainVoiceExampleSchema).default([]);
// Composite — the full BrandBrain content. Mirrors the row in brand_brains
// (id/version/timestamps live as their own columns, not in the JSONB blob).
export const brandBrainContentSchema = z.object({
    identity: brandBrainIdentitySchema,
    voice: brandBrainVoiceSchema,
    customer: brandBrainCustomerSchema,
    assets: brandBrainAssetsSchema,
    history: brandBrainHistorySchema,
    preferences: brandBrainPreferencesSchema,
    /** Phase 4.31 (s221) — structured products & services. Optional in
     *  the parse step (default []) so legacy rows that pre-date the
     *  column parse cleanly. */
    products: brandBrainProductsSchema,
    /** (s284) — structured pricing / plans ({ model, plans }). Sibling
     *  to products (WHAT they sell) but covers HOW they charge. Object
     *  default `{ model:'unknown', plans:[] }` so legacy rows parse
     *  cleanly. */
    pricing: brandBrainPricingSchema,
    /** Phase 4.41 (s231) — structured team / people library. Same
     *  legacy-tolerant default semantics as products. */
    team: brandBrainTeamSchema,
    /** §3.1b Phase 7a (s233) — structured testimonials library. Separate
     *  from the legacy `customer.testimonials` (which keeps its
     *  minimal shape for back-compat). Defaults to [] so brains
     *  written before this field parse cleanly. */
    testimonials: brandBrainTestimonialsSchema,
    /** §3.1b Phase 7b (s233a) — structured voice examples library.
     *  Curated "this is how we sound" anchors. Separate from legacy
     *  voice.voice_examples + guidelines.voiceSamples — those stay
     *  for back-compat; new extractor + creative-agent reads land here. */
    voiceExamples: brandBrainVoiceExamplesSchema,
    /** §3.1c Phase 1 (s236) — Brand Design System. The visual half of
     *  the brain (typography scale, palette pair rules, spacing
     *  rhythm, radius posture, motifs, …). Templates + dynamic-layout
     *  primitives BOTH read every visual choice from here in later
     *  phases. Defaults match today's primitive behavior so legacy
     *  rows render unchanged. */
    designSystem: brandDesignSystemSchema.default(DEFAULT_BRAND_DESIGN_SYSTEM),
});
//# sourceMappingURL=brand-brain.js.map